import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Filter, FileText, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, CITIES } from "./constants";
import { BlogPostForm } from "./BlogPostForm";

export function BlogTab({ password }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [filterCity, setFilterCity] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { password };
      if (filterCity) params.city = filterCity;
      if (filterStatus) params.status = filterStatus;
      const res = await axios.get(`${API}/admin/articles`, { params });
      setPosts(res.data);
    } catch {
      toast.error("Ошибка загрузки статей");
    } finally {
      setLoading(false);
    }
  }, [password, filterCity, filterStatus]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // Listen for prefill event from ContentPlanTab
  useEffect(() => {
    const handler = () => {
      const data = window.__prefillBlogArticle;
      if (data) {
        setEditPost({
          title_ru: data.titleRu || "",
          title_en: data.titleEn || "",
          slug: data.slug || "",
          city: data.city || "",
        });
        setShowForm(true);
        window.__prefillBlogArticle = null;
      }
    };
    window.addEventListener("prefillBlogArticle", handler);
    return () => window.removeEventListener("prefillBlogArticle", handler);
  }, []);

  const handleDelete = async (post) => {
    if (!window.confirm(`Удалить "${post.title_ru}"?`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${post.id}?password=${encodeURIComponent(password)}`);
      toast.success("Статья удалена");
      fetchPosts();
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  const handleEdit = async (post) => {
    try {
      const res = await axios.get(`${API}/admin/articles/${post.id}?password=${encodeURIComponent(password)}`);
      setEditPost(res.data);
      setShowForm(true);
    } catch {
      toast.error("Ошибка загрузки статьи");
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Блог / CMS</h1>
          <p className="text-gray-500 text-sm">{posts.length} статей</p>
        </div>
        <Button
          onClick={() => { setEditPost(null); setShowForm(true); }}
          className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-bold flex items-center gap-2 self-start sm:self-auto"
          data-testid="add-blog-post-btn"
        >
          <Plus className="w-4 h-4" />
          Новая статья
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm mb-6">
        <Filter className="w-4 h-4 text-gray-400 hidden sm:block" />
        <select
          value={filterCity}
          onChange={e => setFilterCity(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:border-pink-400 outline-none bg-white min-w-[120px]"
          data-testid="blog-admin-city-filter"
        >
          <option value="">Все города</option>
          {CITIES.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:border-pink-400 outline-none bg-white min-w-[120px]"
          data-testid="blog-admin-status-filter"
        >
          <option value="">Все статусы</option>
          <option value="draft">Черновики</option>
          <option value="published">Опубликованные</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-10 h-10 border-2 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">Нет статей</p>
          <Button
            onClick={() => { setEditPost(null); setShowForm(true); }}
            className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl"
          >
            Создать первую статью
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <div key={post.id} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4 hover:shadow-sm transition-shadow" data-testid={`blog-admin-post-${post.id}`}>
              {post.cover_image && (
                <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 hidden sm:block">
                  <img src={post.cover_image} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-gray-900 text-sm truncate">{post.title_ru}</h3>
                  {post.status === "published" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      <Eye className="w-3 h-3" />
                      Опубликовано
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      <EyeOff className="w-3 h-3" />
                      Черновик
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  {post.city && <span className="text-pink-500 font-medium">{CITIES.find(c => c.slug === post.city)?.name || post.city}</span>}
                  <span>/{post.slug}</span>
                  {post.tags?.length > 0 && <span className="text-gray-400">| {post.tags.join(", ")}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {post.status === "published" && (
                  <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-pink-500 hover:bg-gray-50 rounded-full transition-colors" data-testid={`blog-view-${post.id}`}>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button onClick={() => handleEdit(post)} className="p-2 text-gray-400 hover:text-pink-500 hover:bg-gray-50 rounded-full transition-colors" data-testid={`blog-edit-${post.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(post)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-50 rounded-full transition-colors" data-testid={`blog-delete-${post.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <BlogPostForm
          post={editPost}
          password={password}
          onSave={() => { setShowForm(false); setEditPost(null); fetchPosts(); }}
          onCancel={() => { setShowForm(false); setEditPost(null); }}
        />
      )}
    </div>
  );
}
